import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { hasPermission } from '../utils/roles';
import ClientMessagesThread from '../components/ClientMessagesThread';
import SheikhIdentityForm from '../components/SheikhIdentityForm';

export default function Clients() {
  const { api, user, impersonateClient } = useAuth();
  // نفس شروط الخادم حرفياً: clients.create / clients.update / clients.impersonate
  const canCreate = hasPermission(user, 'clients.create');
  const canEdit = hasPermission(user, 'clients.update');
  const canImpersonate = hasPermission(user, 'clients.impersonate');
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'sheikh', contact_name: '', contact_phone: '', contact_email: '', notes: '', portal_password: '' });
  const [selected, setSelected] = useState(null);
  const [messaging, setMessaging] = useState(null);

  const load = () => api('/clients').then(setClients).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  // فتح المراسلة مباشرة من رابط الإشعار: /clients?message=<id>
  useEffect(() => {
    const id = searchParams.get('message');
    if (!id || clients.length === 0) return;
    const target = clients.find(c => String(c.id) === String(id));
    if (target) setMessaging(target);
  }, [searchParams, clients]);

  const closeMessaging = () => {
    setMessaging(null);
    if (searchParams.get('message')) setSearchParams({}, { replace: true });
  };

  const save = async () => {
    try {
      const payload = { ...form, portal_password: form.portal_password || null };
      const res = selected
        ? await api(`/clients/${selected.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await api('/clients', { method: 'POST', body: JSON.stringify(payload) });
      if (res?.warning) toast.warning(res.warning);
      else if (form.portal_password) toast.success('تم تعيين كلمة مرور حساب البوابة');
      setShowModal(false);
      setForm({ name: '', type: 'sheikh', contact_name: '', contact_phone: '', contact_email: '', notes: '', portal_password: '' });
      setSelected(null);
      load();
    } catch (err) { toast.error(err.message); }
  };

  // إعادة محاولة ربط حساب البوابة بالبريد الحالي (ينشئ الحساب إن لم يوجد)
  const linkPortal = async (c) => {
    try {
      const res = await api(`/clients/${c.id}`, { method: 'PUT', body: JSON.stringify({ contact_email: c.contact_email }) });
      // كلمة المرور تُحدَّد من نموذج التعديل؛ الربط السريع يرسل رابط تعيين للعميل
      if (res?.warning) toast.warning(res.warning);
      else if (res?.portal_user_id) toast.success('تم ربط حساب البوابة');
      load();
    } catch (err) { toast.error(err.message); }
  };

  // معاينة البوابة بحساب العميل: الجلسة كلها تتحوّل إليه حتى الرجوع من شريط المعاينة
  // الدعوة قرار المدير: حساب واحد للشيخ أو مدير مكتبه، ورابط تعيين كلمة المرور إلى بريده
  const invitePortal = async (c) => {
    const email = window.prompt(`بريد المدعوّ إلى بوابة «${c.name}» (الشيخ أو مدير مكتبه):`, c.contact_email || '');
    if (!email) return;
    const name = window.prompt('اسم المدعوّ:', c.contact_name || c.name);
    if (!name) return;
    try {
      const res = await api(`/clients/${c.id}/portal-invite`, { method: 'POST', body: JSON.stringify({ email, name }) });
      toast.success(res.message);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const openPortalAs = async (c) => {
    if (!window.confirm(`سيتم فتح بوابة "${c.name}" بحسابه، وكل ما تفعله هناك يُسجَّل باسمه. متابعة؟`)) return;
    try {
      await impersonateClient(c.id);
      navigate('/portal');
    } catch (err) { toast.error(err.message); }
  };

  const edit = (c) => {
    setSelected(c);
    setForm({
      name: c.name, type: c.type, contact_name: c.contact_name || '', contact_phone: c.contact_phone || '',
      contact_email: c.contact_email || '', notes: c.notes || '', portal_password: '',
    });
    setShowModal(true);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>العملاء</h2>
        {canCreate && <button className="btn btn-primary" data-tour="clients-create" onClick={() => { setSelected(null); setForm({ name: '', type: 'sheikh', contact_name: '', contact_phone: '', contact_email: '', notes: '', portal_password: '' }); setShowModal(true); }}>+ عميل جديد</button>}
      </div>

      <div className="card">
        {loading ? <div className="loader">جاري التحميل...</div> : clients.length === 0 ? (
          <div className="empty"><div className="empty-icon">👥</div>لا يوجد عملاء</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>الاسم</th><th>النوع</th><th>جهة الاتصال</th><th>حساب البوابة</th><th>مشاريع نشطة</th><th>مهام معلقة</th><th></th></tr></thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td><span className="badge badge-in_progress">{c.type === 'sheikh' ? 'شيخ' : c.type === 'association' ? 'جمعية' : 'أخرى'}</span></td>
                    <td>{c.contact_name || '—'}<br/><span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{c.contact_phone}</span></td>
                    <td>
                      {c.user ? (
                        <span className="badge badge-completed" title={c.user.email}>مرتبط</span>
                      ) : (
                        <>
                          <span className="badge badge-cancelled">غير مرتبط</span>
                          {canEdit && c.contact_email && (
                            <button className="btn btn-sm btn-outline" style={{ marginRight: 6 }} onClick={() => linkPortal(c)}>ربط</button>
                          )}
                          {user.role === 'admin' && c.type === 'sheikh' && (
                            <button className="btn btn-sm btn-primary" style={{ marginRight: 6 }} onClick={() => invitePortal(c)}>دعوة إلى البوابة</button>
                          )}
                          {!c.contact_email && user.role !== 'admin' && <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>أضف بريد التواصل أولاً</div>}
                        </>
                      )}
                    </td>
                    <td>{c.active_projects}</td>
                    <td>{c.pending_tasks}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {canEdit && <button className="btn btn-sm btn-outline" onClick={() => edit(c)}>تعديل</button>}
                      {canEdit && c.user && (
                        <button className="btn btn-sm btn-outline" style={{ marginRight: 6 }} onClick={() => setMessaging(c)}>💬 مراسلة</button>
                      )}
                      {canImpersonate && c.user && (
                        <button className="btn btn-sm btn-outline" style={{ marginRight: 6 }}
                          title="فتح بوابة العميل بحسابه" onClick={() => openPortalAs(c)}>🔑 دخول كالعميل</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {messaging && (
        <div className="modal-overlay" onClick={closeMessaging}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-title">مراسلة: {messaging.name}</div>
            <ClientMessagesThread clientId={messaging.id} />
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={closeMessaging}>إغلاق</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{selected ? 'تعديل عميل' : 'عميل جديد'}</div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">الاسم *</label><input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">النوع</label>
                <select className="form-select" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="sheikh">شيخ</option><option value="association">جمعية</option><option value="other">أخرى</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">اسم جهة الاتصال</label><input className="form-input" value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">الهاتف</label><input className="form-input" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} /></div>
            </div>
            <div className="form-group"><label className="form-label">البريد الإلكتروني (لبوابة العميل)</label><input className="form-input" type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} /></div>
            <div className="form-group">
              <label className="form-label">كلمة مرور حساب البوابة</label>
              <input className="form-input" type="text" autoComplete="new-password" placeholder="٨ أحرف على الأقل"
                value={form.portal_password} onChange={e => setForm(f => ({ ...f, portal_password: e.target.value }))} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {selected?.user
                  ? 'اكتبها لتغيير كلمة مرور حساب البوابة الحالي، أو اتركها فارغة لإبقائها كما هي.'
                  : 'تُستخدم عند إنشاء حساب البوابة. إن تركتها فارغة أُرسل للعميل رابط لتعيينها بنفسه.'}
              </div>
            </div>
            <div className="form-group"><label className="form-label">ملاحظات</label><textarea className="form-textarea" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            {selected && canEdit && form.type === 'sheikh' && <SheikhIdentityForm clientId={selected.id} />}
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={save} disabled={!form.name}>حفظ</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
